/* ==========================================================================
   Helbrede — behaviour layer
   Vanilla ES modules, no framework. Every module is defensive: if its markup
   is not on the page it returns quietly, so sections can be reordered or
   removed in the theme editor without breaking anything else.
   ========================================================================== */
(() => {
  'use strict';

  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const money = (paise) => {
    const n = Math.round(Number(paise || 0)) / 100;
    return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: n % 1 ? 2 : 0 });
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
  })[char]);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- configurable order activity counter ----------------------------- */
  const initOrderPulse = (scope = document) => {
    const roots = scope.matches?.('[data-order-pulse]')
      ? [scope]
      : $$('[data-order-pulse]', scope);
    roots.forEach((root) => {
      if (root.dataset.orderPulseReady === 'true') return;
      root.dataset.orderPulseReady = 'true';

      const numberEl = $('[data-order-pulse-value]', root);
      if (!numberEl) return;
      const read = (name, fallback) => {
        const value = Number(root.dataset[name]);
        return Number.isFinite(value) ? value : fallback;
      };
      const minimum = Math.max(0, Math.round(read('orderPulseMin', 0)));
      const maximum = Math.max(minimum, Math.round(read('orderPulseMax', minimum)));
      const stepMin = Math.max(1, Math.round(read('orderPulseStepMin', 1)));
      const stepMax = Math.max(stepMin, Math.round(read('orderPulseStepMax', stepMin)));
      const delayMin = Math.max(1, read('orderPulseDelayMin', 3));
      const delayMax = Math.max(delayMin, read('orderPulseDelayMax', delayMin));
      const start = Math.min(maximum, Math.max(minimum, Math.round(read('orderPulseStart', minimum))));
      const suffix = root.dataset.orderPulseSuffix || '';
      let count = start;
      let timer;

      const randomBetween = (low, high) => low + Math.random() * (high - low);
      const render = () => { numberEl.textContent = `${count.toLocaleString('en-IN')}${suffix}`; };
      const schedule = () => {
        if (count >= maximum || document.hidden || reduceMotion) return;
        timer = window.setTimeout(() => {
          count = Math.min(maximum, count + Math.max(1, Math.round(randomBetween(stepMin, stepMax))));
          render();
          schedule();
        }, randomBetween(delayMin, delayMax) * 1000);
      };
      const onVisibility = () => {
        if (document.hidden) { window.clearTimeout(timer); timer = undefined; }
        else schedule();
      };

      render();
      schedule();
      document.addEventListener('visibilitychange', onVisibility);
      root.addEventListener('shopify:section:unload', () => {
        window.clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibility);
      }, { once: true });
    });
  };
  initOrderPulse();
  document.addEventListener('shopify:section:load', (event) => initOrderPulse(event.target));

  /* Judge.me's card badge is a five-star row. Product cards use the compact
     one-star score pill from the supplied reference while keeping Judge.me as
     the source of truth. Zero-review products do not get a pretend score. */
  const hydrateRatingOverlays = () => {
    $$('[data-rating-overlay]').forEach((root) => {
      const badge = $('.jdgm-prev-badge', root);
      if (!badge) return;
      const score = Number(badge.dataset.averageRating || 0);
      const count = Number(badge.dataset.numberOfReviews || 0);
      if (score <= 0 || count <= 0) { root.hidden = true; return; }
      root.removeAttribute('data-rating-overlay');
      root.innerHTML = `<span class="product-rating__single-star">★</span><span class="product-rating__score">${score.toFixed(1)}</span><span class="product-rating__count">(${count})</span>`;
    });
  };
  hydrateRatingOverlays();
  setTimeout(hydrateRatingOverlays, 1200);

  /* Judge.me arrives after the theme script, so enhance its real review list
     whenever the app mounts (or replaces it after pagination). Reviews stay in
     Judge.me; this only turns a multi-review list into an accessible rail. */
  const initProductReviewCarousels = () => {
    $$('.jdgm-rev-widg__reviews').forEach((rail) => {
      const reviews = $$('.jdgm-rev', rail);
      if (reviews.length < 2 || rail.dataset.hbReviewCarousel === 'true') return;

      rail.dataset.hbReviewCarousel = 'true';
      rail.classList.add('hb-review-rail');
      rail.tabIndex = 0;
      rail.setAttribute('role', 'region');
      rail.setAttribute('aria-label', 'Customer reviews carousel');
      rail.setAttribute('aria-live', 'off');

      const controls = document.createElement('div');
      controls.className = 'hb-review-controls';
      controls.innerHTML = `
        <span class="hb-review-controls__hint">Swipe or use the arrows to read more</span>
        <span class="hb-review-controls__buttons">
          <button class="hb-review-control" type="button" data-review-prev aria-label="Previous review">←</button>
          <button class="hb-review-control" type="button" data-review-next aria-label="Next review">→</button>
        </span>`;
      rail.before(controls);

      const prev = $('[data-review-prev]', controls);
      const next = $('[data-review-next]', controls);
      const step = () => {
        const first = $('.jdgm-rev', rail);
        if (!first) return rail.clientWidth;
        const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 16;
        return first.getBoundingClientRect().width + gap;
      };
      const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
      const sync = () => {
        const max = maxScroll();
        controls.hidden = max <= 4;
        prev.disabled = rail.scrollLeft <= 4;
        next.disabled = rail.scrollLeft >= max - 4;
      };

      let timer;
      let paused = false;
      const stop = () => clearTimeout(timer);
      const schedule = () => {
        stop();
        if (reduceMotion || paused || document.hidden || maxScroll() <= 4) return;
        timer = setTimeout(() => {
          const max = maxScroll();
          const atEnd = rail.scrollLeft >= max - 8;
          rail.scrollTo({ left: atEnd ? 0 : Math.min(max, rail.scrollLeft + step()), behavior: 'smooth' });
          schedule();
        }, 5800);
      };
      const move = (direction) => {
        rail.scrollBy({ left: direction * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        schedule();
      };

      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', sync, { passive: true });
      rail.addEventListener('pointerenter', () => { paused = true; stop(); });
      rail.addEventListener('pointerleave', () => { paused = false; schedule(); });
      rail.addEventListener('focusin', () => { paused = true; stop(); });
      rail.addEventListener('focusout', () => { paused = false; schedule(); });
      rail.addEventListener('touchstart', () => { paused = true; stop(); }, { passive: true });
      rail.addEventListener('touchend', () => { paused = false; schedule(); }, { passive: true });
      window.addEventListener('resize', () => { sync(); schedule(); });
      document.addEventListener('visibilitychange', schedule);
      sync();
      schedule();
    });
  };
  initProductReviewCarousels();
  const reviewWidgetObserver = new MutationObserver(initProductReviewCarousels);
  reviewWidgetObserver.observe(document.body, { childList: true, subtree: true });

  /* ---------------------------------------------- shoppable product videos */
  const initProductVideoRails = (scope = document) => {
    $$('[data-video-shop]', scope).forEach((root) => {
      if (root.dataset.videoShopReady === 'true') return;
      root.dataset.videoShopReady = 'true';

      const cards = $$('[data-video-card]', root);
      const videos = $$('[data-product-video]', root);
      const rail = $('[data-rail]', root);
      const wantsAutoplay = root.dataset.videoAutoplay === 'true' && !reduceMotion;
      let sectionVisible = false;
      let activeVideo = null;
      let manuallyPaused = null;
      let scrollFrame;

      const sync = (video) => {
        const card = video.closest('[data-video-card]');
        if (!card) return;
        const playing = !video.paused && !video.ended;
        card.classList.toggle('is-playing', playing);
        card.classList.toggle('is-sound-on', !video.muted);
        const playButton = $('[data-video-toggle]', card);
        const soundButton = $('[data-video-sound]', card);
        if (playButton) playButton.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${video.getAttribute('aria-label') || 'product video'}`);
        if (soundButton) soundButton.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} ${video.getAttribute('aria-label') || 'product video'}`);
      };

      const pauseOthers = (active) => {
        $$('[data-product-video]').forEach((video) => {
          if (video !== active && !video.paused) video.pause();
        });
      };

      const play = async (video, mute = false) => {
        pauseOthers(video);
        if (mute) {
          video.defaultMuted = true;
          video.muted = true;
        }
        try { await video.play(); } catch (_) {}
        sync(video);
      };

      const getActiveVideo = () => {
        if (!rail) return videos[0];
        const railRect = rail.getBoundingClientRect();
        let best;

        cards.forEach((card) => {
          const video = $('[data-product-video]', card);
          if (!video) return;
          const rect = card.getBoundingClientRect();
          const visibleWidth = Math.max(0, Math.min(rect.right, railRect.right) - Math.max(rect.left, railRect.left));
          if (visibleWidth <= 0) return;
          const visibleRatio = visibleWidth / Math.max(1, rect.width);
          const leadingEdgeDistance = Math.abs(rect.left - railRect.left);
          const score = (visibleRatio * 10000) - leadingEdgeDistance;
          if (!best || score > best.score) best = { video, card, score };
        });

        return best?.video || videos[0];
      };

      const activateVisibleVideo = () => {
        if (!wantsAutoplay || !sectionVisible || document.hidden) return;
        const nextVideo = getActiveVideo();
        if (!nextVideo) return;

        const changed = activeVideo !== nextVideo;
        if (changed) {
          activeVideo = nextVideo;
          manuallyPaused = null;
        }
        cards.forEach((card) => card.classList.toggle('is-active', card.contains(nextVideo)));
        pauseOthers(nextVideo);
        if (nextVideo.paused && manuallyPaused !== nextVideo) play(nextVideo, true);
      };

      cards.forEach((card) => {
        const video = $('[data-product-video]', card);
        if (!video) return;
        const toggle = $('[data-video-toggle]', card);
        const sound = $('[data-video-sound]', card);

        const togglePlayback = () => {
          if (video.paused) {
            manuallyPaused = null;
            activeVideo = video;
            play(video);
          } else {
            manuallyPaused = video;
            video.pause();
          }
        };
        toggle?.addEventListener('click', togglePlayback);
        video.addEventListener('click', togglePlayback);
        sound?.addEventListener('click', async () => {
          video.muted = !video.muted;
          if (video.paused) await play(video);
          sync(video);
        });
        ['play', 'pause', 'ended', 'volumechange'].forEach((eventName) => video.addEventListener(eventName, () => sync(video)));
        sync(video);
      });

      if (wantsAutoplay && 'IntersectionObserver' in window) {
        const sectionObserver = new IntersectionObserver((entries) => {
          sectionVisible = entries.some((entry) => entry.isIntersecting);
          if (!sectionVisible) videos.forEach((video) => video.pause());
          else {
            manuallyPaused = null;
            activateVisibleVideo();
          }
        }, { threshold: 0.05 });
        sectionObserver.observe(root);

        rail?.addEventListener('scroll', () => {
          cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(activateVisibleVideo);
        }, { passive: true });
        rail?.addEventListener('scrollend', activateVisibleVideo);
        window.addEventListener('resize', activateVisibleVideo);
      }
    });
  };
  initProductVideoRails();
  document.addEventListener('shopify:section:load', (event) => initProductVideoRails(event.target));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) $$('[data-product-video]').forEach((video) => video.pause());
  });

  /* ---------------------------------------------------------------- panels */
  const Panels = (() => {
    const scrim = $('[data-scrim]');
    let open = null;
    let lastFocus = null;

    function show(name) {
      const el = $(`[data-panel="${name}"]`);
      if (!el) return;
      if (open) hide();
      lastFocus = document.activeElement;
      open = el;
      el.classList.add('is-on');
      el.setAttribute('aria-hidden', 'false');
      if (scrim && name !== 'search') { scrim.hidden = false; requestAnimationFrame(() => scrim.classList.add('is-on')); }
      document.body.style.overflow = 'hidden';
      const focusable = el.querySelector('input,button,a[href]');
      setTimeout(() => focusable?.focus({ preventScroll: true }), 120);
      document.querySelectorAll(`[data-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }

    function hide() {
      if (!open) return;
      const name = open.dataset.panel;
      open.classList.remove('is-on');
      open.setAttribute('aria-hidden', 'true');
      scrim?.classList.remove('is-on');
      setTimeout(() => { if (scrim && !open) scrim.hidden = true; }, 380);
      document.body.style.overflow = '';
      document.querySelectorAll(`[data-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      open = null;
      lastFocus?.focus?.({ preventScroll: true });
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (opener) { e.preventDefault(); show(opener.dataset.open); return; }
      if (e.target.closest('[data-close]')) { e.preventDefault(); hide(); }
    });
    scrim?.addEventListener('click', hide);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });

    return { show, hide };
  })();

  /* ------------------------------------------------------------ cart state */
  const Cart = (() => {
    const lines   = $('[data-cart-lines]');
    const empty   = $('[data-cart-empty]');
    const foot    = $('[data-cart-foot]');
    const totalEl = $('[data-cart-total]');
    const bar     = $('[data-ship-bar]');
    const barTxt  = $('[data-ship-text]');
    const barFill = $('[data-ship-fill]');
    const cartPage = $('[data-cart-page]');
    let cartState = null;
    let refreshPromise = null;
    let mutationQueue = Promise.resolve();

    const lineMarkup = (i, page = false) => {
      const variant = i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : '';
      const meta = [variant, i.pack_size].filter(Boolean).join(' · ');
      if (page) {
        return `<li class="cartpage__row" data-key="${escapeHtml(i.key)}" data-quantity="${i.quantity}">
          ${i.image ? `<img src="${escapeHtml(i.image)}" alt="" width="96" height="96" loading="lazy">` : '<div class="cartpage__image"></div>'}
          <div>
            <a class="cartpage__t" href="${escapeHtml(i.url)}">${escapeHtml(i.product_title)}</a>
            ${meta ? `<p class="cl__v">${escapeHtml(meta)}</p>` : ''}
            <button class="cartpage__rm" type="button" data-remove>Remove</button>
          </div>
          <span class="qty">
            <button type="button" data-qty="-1" aria-label="Decrease quantity">−</button>
            <span>${i.quantity}</span>
            <button type="button" data-qty="1" aria-label="Increase quantity">+</button>
          </span>
          <strong class="num">${money(i.line_price)}</strong>
        </li>`;
      }
      return `<li class="cl" data-key="${escapeHtml(i.key)}" data-quantity="${i.quantity}">
        ${i.image ? `<img class="cl__img" src="${escapeHtml(i.image)}" alt="" width="76" height="76">` : '<div class="cl__img"></div>'}
        <div>
          <a class="cl__t" href="${escapeHtml(i.url)}">${escapeHtml(i.product_title)}</a>
          ${meta ? `<div class="cl__v">${escapeHtml(meta)}</div>` : ''}
          <div class="cl__row">
            <span class="qty">
              <button type="button" data-qty="-1" aria-label="Decrease quantity">−</button>
              <span>${i.quantity}</span>
              <button type="button" data-qty="1" aria-label="Increase quantity">+</button>
            </span>
            <span class="cl__price">${money(i.line_price)}</span>
          </div>
        </div>
      </li>`;
    };

    function paint(cart) {
      cartState = cart;
      $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
      $$('[data-cart-count-text]').forEach((el) => { el.textContent = `(${cart.item_count})`; });
      if (!lines) return;

      lines.innerHTML = cart.items.map((i) => lineMarkup(i)).join('');

      const has = cart.item_count > 0;
      if (empty) {
        empty.hidden = has;
        empty.style.display = has ? 'none' : '';
        empty.setAttribute('aria-hidden', String(has));
      }
      if (foot) {
        foot.hidden = !has;
        foot.style.display = has ? '' : 'none';
      }
      if (totalEl) totalEl.textContent = money(cart.total_price);

      if (cartPage) {
        const pageLines = $('[data-cart-page-lines]', cartPage);
        const pageTotal = $('[data-cart-page-total]', cartPage);
        const pageFilled = $('[data-cart-page-filled]', cartPage);
        const pageEmpty = $('[data-cart-page-empty]', cartPage);
        if (pageLines) pageLines.innerHTML = cart.items.map((i) => lineMarkup(i, true)).join('');
        if (pageTotal) pageTotal.textContent = money(cart.total_price);
        if (pageFilled) pageFilled.hidden = !has;
        if (pageEmpty) pageEmpty.hidden = has;
      }
      if (bar && barTxt && barFill) {
        const t = Number(bar.dataset.threshold || 0);
        const left = Math.max(0, t - cart.total_price);
        barTxt.textContent = !has ? `Free delivery on orders over ${money(t)}`
          : left > 0 ? `${money(left)} away from free delivery`
          : 'Free delivery unlocked';
        barFill.style.width = `${Math.min(100, t ? (cart.total_price / t) * 100 : 0)}%`;
      }

      /* Offer progress cards are a readout only. Shopify applies the Any-3 /
         Any-5 price once the cart qualifies. Keep the same live state in the
         offer page and cart drawer so adding an item never forces checkout. */
      const offerIdsEl = document.querySelector('[data-offer-eligible-ids]');
      const offerCards = $$('[data-offer-progress],[data-cart-offer-progress]');
      const floatBag = $('[data-offer-float-bag]');
      if (offerIdsEl && offerCards.length) {
        const requestedTier = new URLSearchParams(window.location.search).get('offer');
        if (requestedTier === '3' || requestedTier === '5') {
          offerCards.forEach((card) => { card.dataset.offerSelected = requestedTier; });
        }
        if (!offerIdsEl._eligible) {
          let ids = [];
          try { ids = JSON.parse(offerIdsEl.textContent || '[]'); } catch (_) { ids = []; }
          offerIdsEl._eligible = new Set((Array.isArray(ids) ? ids : []).map(String));
        }
        const eligible = offerIdsEl._eligible;
        const count = cart.items.reduce((n, i) => n + (eligible.has(String(i.product_id)) ? i.quantity : 0), 0);
        if (floatBag) {
          const floatCounts = $$('[data-offer-float-count]', floatBag);
          const target3 = Number(floatBag.dataset.offerTarget3 || 99900);
          const target5 = Number(floatBag.dataset.offerTarget5 || 149900);
          const tier3Title = $('[data-offer-float-title3]', floatBag);
          const tier3Sub = $('[data-offer-float-sub3]', floatBag);
          const tier3Fill = $('[data-offer-float-fill3]', floatBag);
          const tier3 = $('[data-offer-float-tier="3"]', floatBag);
          const tier5Title = $('[data-offer-float-title5]', floatBag);
          const tier5Sub = $('[data-offer-float-sub5]', floatBag);
          const tier5Fill = $('[data-offer-float-fill5]', floatBag);
          const tier5 = $('[data-offer-float-tier="5"]', floatBag);
          floatBag.hidden = count === 0;
          floatBag.setAttribute('aria-hidden', String(count === 0));
          floatCounts.forEach((badge) => { badge.textContent = String(count); });
          const threeUnlocked = count >= 3;
          const fiveUnlocked = count >= 5;
          floatBag.dataset.offerState = fiveUnlocked ? 'five' : threeUnlocked ? 'three' : 'building';
          if (tier3) tier3.dataset.offerState = threeUnlocked ? 'unlocked' : 'building';
          if (tier3Title) tier3Title.textContent = threeUnlocked
            ? `Unlocked — ${money(target3)} + free gift`
            : `Add ${3 - count} more to unlock ${money(target3)} + free gift`;
          if (tier3Sub) tier3Sub.textContent = threeUnlocked
            ? '3 of 3 eligible products selected'
            : `${count} of 3 eligible products selected`;
          if (tier3Fill) tier3Fill.style.width = `${Math.min(100, (count / 3) * 100)}%`;
          if (tier5) tier5.dataset.offerState = fiveUnlocked ? 'unlocked' : 'building';
          if (tier5Title) tier5Title.textContent = fiveUnlocked
            ? `Unlocked — ${money(target5)} + free gift`
            : `Add ${5 - count} more to unlock ${money(target5)} + free gift`;
          if (tier5Sub) tier5Sub.textContent = fiveUnlocked
            ? '5 of 5 eligible products selected'
            : `${count} of 5 eligible products selected`;
          if (tier5Fill) tier5Fill.style.width = `${Math.min(100, (count / 5) * 100)}%`;
        }
        offerCards.forEach((offerCard) => {
          const target3 = Number(offerCard.dataset.offerTarget3 || 0);
          const target5 = Number(offerCard.dataset.offerTarget5 || 0);
          const selected = offerCard.dataset.offerSelected === '5' ? 5 : 3;
          const title = $('[data-offer-progress-title]', offerCard);
          const sub = $('[data-offer-progress-sub]', offerCard);
          const dotsRoot = $('.offer-progress-card__dots,.cart-offer__dots', offerCard);
          let state = 'building';

          if (selected === 5) {
            if (count >= 5) {
              state = 'five';
              if (title) title.textContent = `Any 5 unlocked — ${money(target5)} + free gift`;
              if (sub) sub.textContent = `${count} eligible product${count === 1 ? '' : 's'} selected`;
            } else {
              if (title) title.textContent = `Add ${5 - count} more to unlock ${money(target5)} + free gift`;
              if (sub) sub.textContent = `${count} of 5 eligible products selected`;
            }
          } else if (count >= 5) {
            state = 'five';
            if (title) title.textContent = `Any 5 unlocked — ${money(target5)} + free gift`;
            if (sub) sub.textContent = `${count} eligible products selected`;
          } else if (count >= 3) {
            state = 'three';
            if (title) title.textContent = `Any 3 unlocked — ${money(target3)} + free gift`;
            if (sub) sub.textContent = `Add ${5 - count} more for Any 5 at ${money(target5)} + free gift`;
          } else {
            if (title) title.textContent = `Add ${3 - count} more to unlock ${money(target3)} + free gift`;
            if (sub) sub.textContent = `${count} of 3 eligible products selected`;
          }
          offerCard.dataset.offerState = state;
          if (dotsRoot) $$('span', dotsRoot).forEach((dot, index) => dot.classList.toggle('is-on', index < Math.min(count, 5)));
        });
      }
    }

    async function refresh() {
      if (refreshPromise) return refreshPromise;
      refreshPromise = fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error('Cart refresh failed');
          return response.json();
        })
        .then((cart) => { paint(cart); return cart; })
        .catch(() => cartState)
        .finally(() => { refreshPromise = null; });
      return refreshPromise;
    }

    const mutate = (job) => {
      const next = mutationQueue.catch(() => {}).then(job);
      mutationQueue = next;
      return next;
    };

    /* fetch resolves on a 4xx, so a sold-out 422 used to fall straight through
       to the drawer — which then opened empty with nothing explaining why.
       Check the status, surface Shopify's own message, and only open the drawer
       on a real add. Returns false so callers can abandon (buy-now must not
       redirect to a checkout the line never reached). */
    function showError(message) {
      const box = $('[data-cart-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = false;
    }

    function clearError() {
      const box = $('[data-cart-error]');
      if (box) { box.textContent = ''; box.hidden = true; }
    }

    async function add(id, quantity = 1, { openCart = true } = {}) {
      return mutate(async () => {
        clearError();
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) }] }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            showError(body.description || body.message || 'Sorry — that could not be added to your cart.');
            return false;
          }
          await refresh();
          if (openCart) Panels.show('cart');
          return true;
        } catch (_) {
          showError('Sorry — something went wrong. Please try again.');
          return false;
        }
      });
    }

    async function addItems(items, { openCart = true, discountCode = '' } = {}) {
      return mutate(async () => {
        clearError();
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            showError(body.description || body.message || 'Sorry — that offer could not be added to your cart.');
            return false;
          }
          if (discountCode) {
            await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
              credentials: 'same-origin',
              redirect: 'follow',
              headers: { Accept: 'text/html' },
            });
          }
          await refresh();
          if (openCart) Panels.show('cart');
          return true;
        } catch (_) {
          showError('Sorry — something went wrong. Please try again.');
          return false;
        }
      });
    }

    async function change(key, quantity) {
      return mutate(async () => {
        const res = await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error('Cart update failed');
        /* Shopify returns the complete cart here. Painting that response avoids
           the old second /cart.js request and removes the full-page reload. */
        const cart = await res.json();
        paint(cart);
        return cart;
      }).catch(() => refresh());
    }

    /* Delegated on the document rather than the drawer list, so the same
       controls work on the cart page, whose rows Liquid renders server-side.
       Quantity is read off the row instead of its text, so both markups agree. */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty],[data-remove]');
      if (!btn) return;
      const row = btn.closest('[data-key]');
      if (!row) return;
      e.preventDefault();
      const cur = Number(row.dataset.quantity || 0);
      const next = btn.hasAttribute('data-remove') ? 0 : Math.max(0, cur + Number(btn.dataset.qty));
      row.dataset.quantity = String(next);
      row.setAttribute('aria-busy', 'true');
      $$('button', row).forEach((control) => { control.disabled = true; });
      const quantityText = $('.qty span', row);
      if (quantityText && next > 0) quantityText.textContent = String(next);
      change(row.dataset.key, next).finally(() => {
        row.removeAttribute('aria-busy');
        $$('button', row).forEach((control) => { control.disabled = false; });
      });
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      e.preventDefault();
      const form = btn.closest('form');
      const id = btn.dataset.variantId || form?.querySelector('[name="id"]')?.value;
      const qty = form?.querySelector('[name="quantity"]')?.value || 1;
      if (!id) return;
      const stayOnPage = btn.dataset.cartBehavior === 'stay';
      const label = btn.querySelector('[data-cart-label]');
      const originalLabel = label ? label.textContent : btn.textContent;
      const status = btn.closest('[data-offer-shop]')?.querySelector('[data-offer-status]');
      btn.classList.add('is-busy');
      btn.disabled = true;
      add(id, qty, { openCart: !stayOnPage }).then((added) => {
        if (!added || !stayOnPage) return;
        if (label) label.textContent = 'Added ✓';
        else btn.textContent = 'Added ✓';
        if (status) status.textContent = `${btn.getAttribute('aria-label')?.replace(/^Add /, '') || 'Product'} added. Choose another product or review your cart.`;
        window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
          else btn.textContent = originalLabel;
        }, 1200);
      }).finally(() => {
        btn.classList.remove('is-busy');
        btn.disabled = false;
      });
    });

    refresh();
    document.addEventListener('hb:offer-tier', (event) => {
      const tier = event.detail?.tier === '5' ? '5' : '3';
      $$('[data-offer-progress],[data-cart-offer-progress]').forEach((card) => { card.dataset.offerSelected = tier; });
      if (cartState) paint(cartState);
    });
    return { add, addItems, refresh };
  })();

  /* ---------------------------------------------------- header mega menu */
  (() => {
    const root = $('[data-drop]');
    if (!root) return;
    const btn = $('[data-drop-btn]', root);
    const panel = $('[data-drop-panel]', root);
    if (!btn || !panel) return;
    let closeTimer;

    function open() {
      clearTimeout(closeTimer);
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-on'));
      btn.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.classList.remove('is-on');
      btn.setAttribute('aria-expanded', 'false');
      closeTimer = setTimeout(() => { panel.hidden = true; }, 240);
    }
    const toggle = () => (btn.getAttribute('aria-expanded') === 'true' ? close() : open());

    btn.addEventListener('click', (e) => { e.preventDefault(); toggle(); });

    /* hover is a convenience on pointer devices; click and keyboard still work */
    if (window.matchMedia('(hover: hover)').matches) {
      root.addEventListener('mouseenter', open);
      root.addEventListener('mouseleave', close);
      panel.addEventListener('mouseenter', open);
      panel.addEventListener('mouseleave', close);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') { close(); btn.focus(); }
    });
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target) && !panel.contains(e.target)) close();
    });
    panel.addEventListener('focusout', (e) => {
      if (!panel.contains(e.relatedTarget) && !root.contains(e.relatedTarget)) close();
    });
  })();

  /* ------------------------------------------------------- hero banner --- */
  (() => {
    const root = $('[data-hero]');
    if (!root) return;
    const slides = $$('[data-hero-slide]', root);
    const dots   = $$('[data-hero-dot]', root);
    if (slides.length < 2) return;
    const autoplay = root.dataset.autoplay !== 'false';
    const interval = Math.max(3000, Number(root.dataset.interval) || 5000);
    let i = 0, timer;

    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => {
        const on = k === i;
        s.classList.toggle('is-on', on);
        s.setAttribute('aria-hidden', String(!on));
        /* a hidden slide is still a link — keep it out of the tab order */
        if (on) s.removeAttribute('tabindex');
        else s.setAttribute('tabindex', '-1');
      });
      dots.forEach((d, k) => {
        d.classList.toggle('is-on', k === i);
        d.setAttribute('aria-selected', String(k === i));
      });
    }

    const start = () => {
      if (reduceMotion || !autoplay) return;
      clearInterval(timer);
      timer = setInterval(() => go(i + 1), interval);
    };
    const stop = () => clearInterval(timer);

    dots.forEach((d, k) => d.addEventListener('click', () => { stop(); go(k); start(); }));
    $('[data-hero-next]', root)?.addEventListener('click', () => { stop(); go(i + 1); start(); });
    $('[data-hero-prev]', root)?.addEventListener('click', () => { stop(); go(i - 1); start(); });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);

    /* swipe on touch */
    let x0 = null;
    root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; stop(); }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) go(dx < 0 ? i + 1 : i - 1);
      x0 = null;
      start();
    }, { passive: true });

    go(0);
    start();
  })();

  /* ------------------------------------------------ campaign offer carousel */
  (() => {
    const root = $('[data-offer-carousel]');
    if (!root) return;
    const slides = $$('[data-offer-slide]', root);
    const dots = $$('[data-offer-dot]', root);
    if (slides.length < 2) return;
    const autoplay = root.dataset.autoplay !== 'false';
    const interval = Math.max(3000, Number(root.dataset.interval) || 5000);
    const requestedTier = new URLSearchParams(window.location.search).get('offer');
    let i = requestedTier === '5' ? 1 : 0, timer, x0 = null;

    const go = (next, syncTier = false) => {
      i = (next + slides.length) % slides.length;
      const tier = slides[i]?.dataset.offerTier === '5' ? '5' : '3';
      slides.forEach((slide, index) => {
        const active = index === i;
        slide.classList.toggle('is-on', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.setAttribute('tabindex', active ? '0' : '-1');
      });
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-on', index === i);
        dot.setAttribute('aria-selected', String(index === i));
      });
      if (syncTier) {
        const url = new URL(window.location.href);
        url.searchParams.set('offer', tier);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        /* Update the URL before notifying the cart module. Its repaint reads
           the query parameter, so the progress card and floating bag always
           use the tier the shopper just selected. */
        window.dispatchEvent(new CustomEvent('hb:offer-tier', { detail: { tier } }));
      }
    };
    const stop = () => clearInterval(timer);
    const start = () => {
      if (reduceMotion || !autoplay || document.hidden) return;
      stop();
      timer = setInterval(() => go(i + 1), interval);
    };

    $('[data-offer-prev]', root)?.addEventListener('click', () => { stop(); go(i - 1, true); start(); });
    $('[data-offer-next]', root)?.addEventListener('click', () => { stop(); go(i + 1, true); start(); });
    dots.forEach((dot, index) => dot.addEventListener('click', () => { stop(); go(index, true); start(); }));
    slides.forEach((slide, index) => {
      const choose = () => {
        stop();
        go(index, true);
        document.querySelector('#offer-products')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        start();
      };
      slide.addEventListener('click', choose);
      slide.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(); }
      });
    });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    root.addEventListener('touchstart', (event) => { x0 = event.touches[0].clientX; stop(); }, { passive: true });
    root.addEventListener('touchend', (event) => {
      if (x0 === null) return;
      const dx = event.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) go(dx < 0 ? i + 1 : i - 1, true);
      x0 = null;
      start();
    }, { passive: true });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());

    go(i, true);
    start();
  })();

  /* ------------------------------------------------------- announcement bar */
  (() => {
    const root = $('[data-anno]');
    if (!root) return;
    const items = $$('[data-anno-item]', root);
    if (items.length < 2) return;
    let i = 0, timer;

    const go = (n) => {
      items[i].classList.remove('is-on');
      i = (n + items.length) % items.length;
      items[i].classList.add('is-on');
    };
    const start = () => { if (!reduceMotion) timer = setInterval(() => go(i + 1), 6000); };
    const stop = () => clearInterval(timer);

    $('[data-anno-next]', root)?.addEventListener('click', () => { stop(); go(i + 1); start(); });
    $('[data-anno-prev]', root)?.addEventListener('click', () => { stop(); go(i - 1); start(); });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    start();
  })();

  /* -------------------------------------------------- rotating placeholder */
  (() => {
    const inputs = $$('[data-rotating-placeholder]');
    if (!inputs.length || reduceMotion) return;
    inputs.forEach((input) => {
      let list;
      try { list = JSON.parse(input.dataset.rotatingPlaceholder); } catch { return; }
      if (!Array.isArray(list) || !list.length) return;
      let i = 0;
      setInterval(() => {
        if (document.activeElement === input || input.value) return;
        i = (i + 1) % list.length;
        input.placeholder = `Search for “${list[i]}”`;
      }, 2800);
    });
  })();

  /* ------------------------------------------------------ predictive search */
  (() => {
    const forms = $$('[data-search-form]');
    if (!forms.length) return;

    forms.forEach((form) => {
      const input = form.querySelector('input[type="search"]');
      const box = form.querySelector('[data-suggest]') || form.parentElement.querySelector('[data-suggest]');
      if (!input || !box) return;
      let t;

      input.addEventListener('input', () => {
        clearTimeout(t);
        const q = input.value.trim();
        if (q.length < 2) { box.hidden = true; box.innerHTML = ''; return; }
        t = setTimeout(async () => {
          try {
            const r = await (await fetch(`/search/suggest?q=${encodeURIComponent(q)}`)).json();
            if (!r.products.length && !r.concerns.length) {
              box.innerHTML = `<p class="sg__empty">Nothing matched “${q}”. Try a concern or an ingredient.</p>`;
            } else {
              box.innerHTML = [
                ...r.concerns.map((c) => `<a class="sg" href="${'/collections/' + c.slug}">
                    <div><div class="sg__t">${c.label}</div><div class="sg__s">Concern · ${c.count} products</div></div></a>`),
                ...r.products.map((p) => `<a class="sg" href="${p.url}">
                    ${p.image ? `<img src="${p.image}" alt="" width="42" height="42">` : '<div class="sg__img"></div>'}
                    <div><div class="sg__t">${p.title}</div><div class="sg__s">${p.subtitle || ''}</div></div>
                    <span class="sg__p">${p.price}</span></a>`),
              ].join('');
            }
            box.hidden = false;
          } catch (_) { box.hidden = true; }
        }, 180);
      });

      document.addEventListener('click', (e) => {
        if (!form.contains(e.target)) box.hidden = true;
      });
    });
  })();

  /* ------------------------------------------------------------- accordions */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.acc__btn');
    if (!btn) return;
    const panel = btn.nextElementSibling;
    if (!panel) return;
    const openNow = btn.getAttribute('aria-expanded') === 'true';

    if (btn.closest('[data-acc-single]') && !openNow) {
      $$('.acc__btn[aria-expanded="true"]', btn.closest('[data-acc-single]')).forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
        b.nextElementSibling.style.height = '0px';
      });
    }
    btn.setAttribute('aria-expanded', String(!openNow));
    panel.style.height = openNow ? '0px' : `${panel.firstElementChild.offsetHeight}px`;
  });

  /* ------------------------------------------------------------ reveal ---- */
  (() => {
    const targets = $$('[data-reveal],[data-reveal-stagger]');
    if (!targets.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    targets.forEach((t) => io.observe(t));

    /* Nothing may stay invisible because an observer never fired — a page
       loaded in a background tab, a prerender, a browser quirk. Content
       always wins over the animation. */
    setTimeout(() => targets.forEach((t) => t.classList.add('is-shown')), 4000);
  })();

  /* --------------------------------------------------------- rail controls */
  $$('[data-rail]').forEach((rail) => {
    const wrap = rail.closest('[data-rail-wrap]') || rail.parentElement;
    const scope = rail.closest('section') || wrap;
    const prev = scope?.querySelector('[data-rail-prev]');
    const next = scope?.querySelector('[data-rail-next]');
    if (!prev || !next) return;

    const step = () => rail.firstElementChild?.offsetWidth + 16 || 280;
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 4;
      prev.disabled = rail.scrollLeft <= 4;
      next.disabled = rail.scrollLeft >= max;
      const idle = max <= 4;
      prev.hidden = next.hidden = idle;
    };
    let autoTimer;
    let autoPaused = false;
    const autoScroll = rail.closest('.tst') && rail.dataset.autoscroll === 'true';
    const autoInterval = Math.max(4000, Number(rail.dataset.autoscrollInterval) || 6000);
    const stopAuto = () => clearTimeout(autoTimer);
    const scheduleAuto = () => {
      stopAuto();
      if (!autoScroll || reduceMotion || autoPaused || document.hidden) return;
      autoTimer = setTimeout(() => {
        const max = rail.scrollWidth - rail.clientWidth;
        if (max <= 4) return;
        const atEnd = rail.scrollLeft >= max - 8;
        rail.scrollTo({ left: atEnd ? 0 : Math.min(max, rail.scrollLeft + step()), behavior: 'smooth' });
        scheduleAuto();
      }, autoInterval);
    };
    const manualScroll = (direction) => {
      rail.scrollBy({ left: direction * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      scheduleAuto();
    };
    prev.addEventListener('click', () => manualScroll(-1));
    next.addEventListener('click', () => manualScroll(1));
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    rail.addEventListener('pointerenter', () => { autoPaused = true; stopAuto(); });
    rail.addEventListener('pointerleave', () => { autoPaused = false; scheduleAuto(); });
    rail.addEventListener('focusin', () => { autoPaused = true; stopAuto(); });
    rail.addEventListener('focusout', () => { autoPaused = false; scheduleAuto(); });
    rail.addEventListener('touchstart', () => { autoPaused = true; stopAuto(); }, { passive: true });
    rail.addEventListener('touchend', () => { autoPaused = false; scheduleAuto(); }, { passive: true });
    document.addEventListener('visibilitychange', scheduleAuto);
    sync();
    scheduleAuto();
  });

  /* ------------------------------------------------------------ tab groups */
  $$('[data-tabs]').forEach((group) => {
    const btns = $$('[data-tab]', group);
    const panes = $$('[data-tab-pane]', group);
    btns.forEach((btn) => btn.addEventListener('click', () => {
      const key = btn.dataset.tab;
      btns.forEach((b) => { b.classList.toggle('is-on', b === btn); b.setAttribute('aria-pressed', String(b === btn)); });
      panes.forEach((p) => { p.hidden = p.dataset.tabPane !== key; });
      group.dispatchEvent(new CustomEvent('tabchange', { detail: { key } }));
    }));
  });

  /* ------------------------------------------------------ collection grid */
  (() => {
    const root = $('[data-collection]');
    const grid = $('[data-grid]');
    if (!root || !grid) return;
    const items = $$('[data-item]', grid);
    const countEl = $('[data-count]');
    const noneEl = $('[data-none]');
    let filter = '*';

    const itemTags = (it) => (it.dataset.filters || '').split('|');

    /* Shopify's all_tags can include values from products outside the current
       paginated grid, or malformed legacy tags. Never offer a filter that has
       no matching product in the rendered collection. */
    $$('[data-filter]', root).forEach((btn) => {
      if (btn.dataset.filter === '*') return;
      if (!items.some((it) => itemTags(it).includes(btn.dataset.filter))) btn.remove();
    });

    function apply() {
      let shown = 0;
      items.forEach((it) => {
        const tags = itemTags(it);
        const on = filter === '*' || tags.includes(filter);
        it.hidden = !on;
        if (on) shown++;
      });
      if (countEl) {
        countEl.hidden = filter === '*';
        countEl.textContent = `${shown} product${shown === 1 ? '' : 's'} in ${filter}`;
      }
      if (noneEl) noneEl.hidden = shown > 0;
    }

    $$('[data-filter]', root).forEach((btn) => btn.addEventListener('click', () => {
      filter = btn.dataset.filter;
      $$('[data-filter]', root).forEach((b) => {
        b.classList.toggle('is-on', b === btn);
        b.setAttribute('aria-pressed', String(b === btn));
      });
      apply();
    }));

    $('[data-sort]', root)?.addEventListener('change', (e) => {
      const how = e.target.value;
      const sorted = [...items].sort((a, b) => {
        if (how === 'price-asc')  return Number(a.dataset.price) - Number(b.dataset.price);
        if (how === 'price-desc') return Number(b.dataset.price) - Number(a.dataset.price);
        if (how === 'name')       return a.dataset.name.localeCompare(b.dataset.name);
        return 0;
      });
      sorted.forEach((el) => grid.appendChild(el));
    });
  })();

  /* =========================================================== product page */

  /* --- gallery --- */
  (() => {
    const stage = $('.pdp__stage');
    if (!stage) return;
    const shots = $$('.pdp__shot', stage);
    const thumbs = $$('.pdp__thumb');
    if (!shots.length) return;
    let i = 0;

    /* Wraps at both ends, the same way the hero does, so the arrows never dead
       -end on the first or last image. The thumbnails were the only way through
       the gallery before this; on a phone they sit below the fold. */
    function show(n) {
      i = (n + shots.length) % shots.length;
      shots.forEach((s, k) => s.classList.toggle('is-on', k === i));
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-on', k === i);
        t.setAttribute('aria-selected', String(k === i));
      });
    }

    thumbs.forEach((t, k) => t.addEventListener('click', () => show(k)));
    $('[data-shot-prev]', stage)?.addEventListener('click', () => show(i - 1));
    $('[data-shot-next]', stage)?.addEventListener('click', () => show(i + 1));
  })();

  /* --- read more --- */
  (() => {
    const btn = $('[data-clamp-toggle]');
    const box = $('[data-clamp]');
    if (!btn || !box) return;
    btn.addEventListener('click', () => {
      const open = box.classList.toggle('is-open');
      btn.textContent = open ? 'Read less' : 'Read more';
      btn.setAttribute('aria-expanded', String(open));
    });
  })();

  /* --- quantity ---
     The pack ladder that used to live here went with its markup: every product
     is a single stocked bottle, so there is no variant to switch between. If
     real pre-packed SKUs ever exist, the selector has to set both the add-to-
     cart dataset and the form's hidden id input — the wallet buttons submit the
     form directly and would otherwise check out the first-rendered variant. */
  (() => {
    const qtyVal = $('[data-qty-value]');
    const qtyInput = $('[data-qty-input]');
    const packs = $$('[data-pack-qty]');

    const savings = $('[data-qty-savings]');
    const liveOut = savings ? $('[data-qty-total]', savings) : null;
    const tierRows = $$('[data-set-qty]');
    const unitPrice = savings ? Number(savings.dataset.unit) : 0;
    const tier2Pct = savings ? Number(savings.dataset.tier2) : 0;
    const tier3Pct = savings ? Number(savings.dataset.tier3) : 0;

    /* Format through the shop's own money_format so these figures cannot
       disagree with the prices Liquid rendered on the same page. en-IN gives
       the lakh grouping Shopify uses for rupees; a hand-rolled three-digit
       grouping would agree up to ₹99,999 and then quietly diverge. */
    /* The symbol and any spacing come off a price Liquid already rendered
       (₹1,000.00, or Rs. 1,000.00 — the live shop uses the latter), so these
       figures cannot disagree with the ones printed beside them. Passing
       shop.money_format instead would ship a literal "{{amount}}" into the
       markup. en-IN gives the lakh grouping Shopify uses for rupees; a
       hand-rolled three-digit grouping agrees up to 99,999 and then diverges. */
    const sample = (savings && savings.dataset.moneySample) || '';
    const firstDigit = sample.search(/\d/);
    const lastDigit = sample.search(/\d(?!.*\d)/);
    const prefix = firstDigit >= 0 ? sample.slice(0, firstDigit) : '';
    const suffix = lastDigit >= 0 ? sample.slice(lastDigit + 1) : '';
    const money = (cents) => prefix + new Intl.NumberFormat('en-IN',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + suffix;

    /* The ladder tops out: three units and up all earn the same 15%, which is
       what the live cart actually bills at four. Promising more would be a
       discount Shopify never applies. */
    const pctFor = (n) => (n >= 3 ? tier3Pct : n === 2 ? tier2Pct : 0);

    function renderSavings(n) {
      tierRows.forEach((row) => {
        const on = Number(row.dataset.setQty) === n
          || (n > 3 && Number(row.dataset.setQty) === 3);
        row.classList.toggle('is-on', on);
      });
      if (!liveOut) return;
      if (n < 2) { liveOut.hidden = true; return; }
      const pct = pctFor(n);
      const gross = unitPrice * n;
      const net = Math.round((gross * (100 - pct)) / 100);
      liveOut.hidden = false;
      /* Shopify applies these in the cart rather than in the line price, so say
         so plainly instead of implying this is what the product page charges. */
      liveOut.textContent = pct > 0
        ? `${n} × ${money(unitPrice)} = ${money(net)} — ${pct}% off applied in your cart`
        : `${n} × ${money(unitPrice)} = ${money(gross)}`;
    }


    /* The ladder and the stepper drive the same number, so they have to agree
       in both directions — otherwise the highlighted pack can contradict the
       quantity actually being added. */
    function setQty(n) {
      const next = Math.max(1, Number(n) || 1);
      if (qtyVal) qtyVal.textContent = next;
      if (qtyInput) qtyInput.value = next;
      packs.forEach((p) => {
        const on = Number(p.dataset.packQty) === next;
        p.classList.toggle('is-on', on);
        p.setAttribute('aria-checked', String(on));
      });
      renderSavings(next);
    }

    packs.forEach((p) => p.addEventListener('click', () => setQty(p.dataset.packQty)));
    tierRows.forEach((row) => row.addEventListener('click', () => setQty(row.dataset.setQty)));

    $$('[data-qty-step]').forEach((btn) => btn.addEventListener('click', () => {
      if (!qtyVal || !qtyInput) return;
      setQty(Number(qtyVal.textContent) + Number(btn.dataset.qtyStep));
    }));

    /* The custom "Buy now" was replaced by Shopify's own accelerated checkout
       buttons, which submit the product form directly — no handler needed. */
  })();

  /* --- delivery estimate ---
     There is no courier serviceability feed, so this is an honest estimate
     from the pincode's postal region rather than a promise. */
  (() => {
    const root = $('[data-pincode]');
    if (!root) return;
    const input = $('[data-pin-input]', root);
    const out = $('[data-pin-out]', root);
    const btn = $('[data-pin-check]', root);

    const METRO = /^(11|12|16|14|40|41|56|60|50|70|38|39|30|20|22|26|48)/;

    function check() {
      const pin = (input.value || '').trim();
      out.hidden = false;
      if (!/^[1-9][0-9]{5}$/.test(pin)) {
        out.classList.add('is-bad');
        out.textContent = 'Enter a valid 6-digit pincode.';
        return;
      }
      out.classList.remove('is-bad');
      const days = METRO.test(pin) ? 3 : 5;
      const d = new Date();
      d.setDate(d.getDate() + days);
      const when = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      out.textContent = `Estimated delivery by ${when} · Cash on delivery available`;
    }

    btn?.addEventListener('click', check);
    input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } });
  })();

  /* --- safety & interaction check ---
     Matches the buyer's declared situation against THIS product's own safety
     copy. Nothing is generated; a non-match says so rather than reassuring. */
  (() => {
    const root = $('[data-safety]');
    if (!root) return;
    let lines = [];
    try { lines = JSON.parse(root.dataset.lines) || []; } catch { return; }
    const out = $('[data-safety-out]', root);

    /* `not` excludes lines that merely contain the keyword in an unrelated
       sense — "keep out of reach of children" is a storage instruction, not an
       age contraindication, and surfacing it as one would be misleading. */
    const RULES = {
      'Pregnant or breastfeeding': { re: /pregnan|breastfeed|lactat|nursing/i },
      'Thyroid condition':         { re: /thyroid/i },
      'Diabetes medication':       { re: /diabet|blood sugar|glyc|insulin/i },
      'Blood pressure medication': { re: /blood pressure|hypertens|hypotens/i },
      'Sedatives or sleep aids':   { re: /sedat|drowsi|anticonvuls|tranquil/i },
      'Under 18':                  { re: /child|under 18|18 year|minor|paediatr|pediatr/i,
                                     not: /keep out of reach|store in a cool/i },
      'Surgery scheduled':         { re: /surgery|surgical|an(a)?esthe|bleeding|blood thin/i },
    };

    const active = new Set();

    function paint() {
      if (!active.size) { out.hidden = true; out.innerHTML = ''; return; }
      const blocks = [...active].map((cond) => {
        const rule = RULES[cond];
        const hits = lines.filter((l) => rule && rule.re.test(l) && !(rule.not && rule.not.test(l)));
        if (hits.length) {
          return hits.map((h) => `<div class="safety-hit">
              <div class="safety-hit__c">${cond}</div>
              <p class="safety-hit__t">${h}</p></div>`).join('');
        }
        return `<div class="safety-hit" style="border-left-color:var(--gold);background:var(--gold-wash)">
            <div class="safety-hit__c" style="color:var(--gold-deep)">${cond}</div>
            <p class="safety-hit__t">This product’s safety information does not mention
            ${cond.toLowerCase()} specifically. That is not the same as “safe for you” —
            please confirm with a qualified healthcare professional before use.</p></div>`;
      });
      out.innerHTML = blocks.join('');
      out.hidden = false;
    }

    $$('[data-cond]', root).forEach((chip) => chip.addEventListener('click', () => {
      const c = chip.dataset.cond;
      const on = active.has(c);
      on ? active.delete(c) : active.add(c);
      chip.classList.toggle('is-on', !on);
      chip.setAttribute('aria-pressed', String(!on));
      paint();
    }));
  })();

  /* --- routine builder --- */
  (() => {
    const root = $('[data-ritual]');
    if (!root) return;
    const totalEl = $('[data-ritual-total]');
    const addBtn = $('[data-ritual-add]');
    const items = () => $$('[data-ritual-item]', root);

    function total() {
      const sum = items().filter((i) => i.checked).reduce((n, i) => n + Number(i.dataset.price), 0);
      if (totalEl) totalEl.textContent = money(sum);
      if (addBtn) addBtn.disabled = sum === 0;
    }

    root.addEventListener('change', total);

    addBtn?.addEventListener('click', async () => {
      const chosen = items().filter((i) => i.checked).map((i) => ({ id: Number(i.value), quantity: 1 }));
      if (!chosen.length) return;
      addBtn.disabled = true;
      try {
        await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: chosen }),
        });
        await Cart.refresh();
        Panels.show('cart');
      } finally { addBtn.disabled = false; }
    });

    total();
  })();

  /* ============================================================ tool pages */

  const CATALOG = (() => {
    const el = document.getElementById('hb-catalog');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  })();

  /* Topical vs ingestible — the split that makes a cross-category routine work. */
  const isTopical = (p) => /serum|oil|cream|gel|lotion/i.test(p.t);

  /* --- ritual builder --- */
  (() => {
    const root = $('[data-rb]');
    if (!root || !CATALOG) return;

    const list   = $('[data-rb-list]', root);
    const totEl  = $('[data-rb-total]', root);
    const descEl = $('[data-rb-desc]', root);
    const noteEl = $('[data-rb-note]', root);
    const addBtn = $('[data-rb-add]', root);
    const errorEl = $('[data-rb-error]', root);

    let concern = $('[data-rb-concern]', root)?.dataset.rbConcern;
    let depth = 3;
    let picks = [];

    /* Some live products are merchandised in a broader collection. Keep the
       ritual useful when a dedicated concern collection is temporarily empty. */
    const concernFallbacks = {
      'digestion-gut': /digest|enzyme|gut|probiotic|tummy|papain|diastase/i,
    };

    function build() {
      const fallback = concernFallbacks[concern];
      const inConcern = CATALOG.filter((p) =>
        p.av !== false && (p.c === concern || (fallback && fallback.test(p.t)))
      );
      const primary = inConcern[0];
      if (!primary) { picks = []; return; }

      const chosen = [primary];
      /* prefer the opposite form for the second slot — a serum next to a tablet */
      const opposite = inConcern.find((p) => p !== primary && isTopical(p) !== isTopical(primary));
      const second = opposite || inConcern[1];
      if (second) chosen.push(second);

      const daily = CATALOG.find((p) => p.c === 'daily-essentials' && p.av !== false && !chosen.includes(p));
      if (daily) chosen.push(daily);

      const night = CATALOG.find((p) => (p.c === 'sleep-stress' || p.c === 'liver-detox') && p.av !== false && !chosen.includes(p));
      if (night) chosen.push(night);

      const SLOTS = ['Morning', 'Evening', 'Daily', 'Night'];
      picks = chosen.slice(0, depth).map((p, i) => ({
        p,
        slot: SLOTS[i] || 'Daily',
        note: i === 0 ? 'The formula for the concern you picked'
            : i === 1 ? (isTopical(p) ? 'The topical half of the same routine' : 'Works alongside it on the same concern')
            : i === 2 ? 'The daily base everything sits on'
            : 'Recovery while you sleep',
        on: true,
      }));
    }

    function paint() {
      list.innerHTML = picks.map((r, i) => `
        <label class="rit">
          <input class="sr" type="checkbox" ${r.on ? 'checked' : ''} data-rb-item="${i}">
          <span class="rit__slot">${r.slot}</span>
          ${r.p.img ? `<img class="rit__img" src="${r.p.img}" alt="" width="64" height="64" loading="lazy">` : '<span class="rit__img"></span>'}
          <span>
            <span class="rit__t">${r.p.t}</span>
            <span class="rit__n">${r.note}</span>
          </span>
          <span class="rit__p">${money(r.p.price)}</span>
        </label>`).join('');

      const on = picks.filter((r) => r.on);
      const sum = on.reduce((n, r) => n + r.p.price, 0);
      totEl.textContent = on.length ? money(sum) : '—';
      addBtn.disabled = !on.length;

      const label = $(`[data-rb-concern="${concern}"]`, root)?.textContent.trim() || concern;
      descEl.textContent = on.length
        ? `${on.length} formulas for ${label}: ${on.map((r) => r.p.t).join(', ')}.`
        : 'Tick at least one product to build a routine.';

      const topicals = on.filter((r) => isTopical(r.p)).length;
      noteEl.textContent = topicals && topicals < on.length
        ? 'This routine works from both directions — something you take and something you apply.'
        : 'Every formula here is taken orally. Add a serum or oil for a topical layer.';
    }

    root.addEventListener('click', (e) => {
      const c = e.target.closest('[data-rb-concern]');
      if (c) {
        concern = c.dataset.rbConcern;
        $$('[data-rb-concern]', root).forEach((b) => {
          b.classList.toggle('is-on', b === c);
          b.setAttribute('aria-pressed', String(b === c));
        });
        build(); paint(); return;
      }
      const d = e.target.closest('[data-rb-depth]');
      if (d) {
        depth = Number(d.dataset.rbDepth);
        $$('[data-rb-depth]', root).forEach((b) => {
          b.classList.toggle('is-on', b === d);
          b.setAttribute('aria-pressed', String(b === d));
        });
        build(); paint();
      }
    });

    list.addEventListener('change', (e) => {
      const cb = e.target.closest('[data-rb-item]');
      if (!cb) return;
      picks[Number(cb.dataset.rbItem)].on = cb.checked;
      paint();
    });

    addBtn.addEventListener('click', async () => {
      const items = picks.filter((r) => r.on).map((r) => ({ id: r.p.vid, quantity: 1 }));
      if (!items.length) return;
      addBtn.disabled = true;
      if (errorEl) errorEl.hidden = true;
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'This routine could not be added. Please try again.');
        await Cart.refresh();
        Panels.show('cart');
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message || 'This routine could not be added. Please try again.';
          errorEl.hidden = false;
        }
      } finally { addBtn.disabled = false; }
    });

    build(); paint();
  })();

  /* --- label compare --- */
  (() => {
    const root = $('[data-compare]');
    if (!root || !CATALOG) return;
    const selA = $('[data-cmp="a"]', root);
    const selB = $('[data-cmp="b"]', root);
    const out = $('[data-cmp-out]', root);

    const options = CATALOG.map((p, i) => `<option value="${i}">${p.t}</option>`).join('');
    selA.innerHTML = options;
    selB.innerHTML = options;
    selA.selectedIndex = 0;
    selB.selectedIndex = Math.min(1, CATALOG.length - 1);

    function panel(p) {
      const dosed = p.ing.filter((i) => i.d).length;
      return `<div class="cmp__card">
        <div class="cmp__hd">
          ${p.img ? `<img src="${p.img}" alt="" width="56" height="56">` : ''}
          <div>
            <div class="cmp__t">${p.t}</div>
            <div class="cmp__p">${money(p.price)} · ${p.pack || ''}</div>
          </div>
        </div>
        <div class="cmp__stat">
          <span><b>${p.ing.length}</b>actives listed</span>
          <span><b>${dosed}</b>with a declared dose</span>
        </div>
        <div class="label-panel">
          <div class="label-panel__hd">
            <span class="label-panel__t">Supplement facts</span>
            <span class="label-panel__s">${p.pack || ''}</span>
          </div>
          <div class="label-panel__rule"></div>
          ${p.ing.length ? p.ing.map((i) => `<div class="label-row">
              <span class="label-row__n">${i.n}</span>
              <span class="label-row__d">${i.d ? i.d : '<em class="t-dim-3">not declared</em>'}</span>
            </div>`).join('')
            : '<div class="label-row"><span class="label-row__n">No ingredient list published for this product.</span></div>'}
        </div>
        <a class="btn btn--ghost btn--sm" href="/products/${p.h}">View ${p.t}</a>
      </div>`;
    }

    function paint() {
      out.innerHTML = panel(CATALOG[selA.value]) + panel(CATALOG[selB.value]);
    }
    selA.addEventListener('change', paint);
    selB.addEventListener('change', paint);
    paint();
  })();

  window.Helbrede = { Cart, Panels, money };
})();
